<template>
  <Slideover :visible="visible" :size="'max-w-3xl'">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Slideover Body -->
    <div class="relative px-4 py-2">
      <div class="space-y-2" v-if="this.products">
        <div class="flex flex-col">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg"
              >
                <table class="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th>Product</th>
                      <th>Size</th>
                      <th>Quantity</th>
                      <th>Facility</th>
                      <th>Locked</th>
                    </tr>
                  </thead>
                  <tbody
                    class="bg-white divide-y divide-gray-200"
                  >
                    <tr
                      class="text-center"
                      v-for="(orderDetail, index) in orderDetails"
                      :key="index"
                    >
                      <td>
                        {{ orderDetail.variant_id }}
                      </td>
                      <td class="limited-width">
                        {{ orderDetail.variant.product.name }}
                      </td>
                      <td>
                        {{ orderDetail.variant.variant_name }}
                      </td>
                      <td>
                        {{ orderDetail.QuantityOrdered }}
                      </td>
                      <td class="fixed-column">
                        <div class="mt-1 relative rounded-md shadow-sm">
                          <select
                            :id="`qoh_${_uid}`"
                            v-model="orderDetail.SelectedFacilityId"
                            @change="validateForRestrictions"
                            :disabled="
                              loadingRestrictions || isShipped(orderDetail)
                            "
                            class="mt-1 form-select block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                          >
                            <option
                              v-for="facility in orderDetail.facilities"
                              :key="facility.id"
                              :value="facility.id"
                              :disabled="facility.qoh === 0"
                            >
                              {{ facility.name }} : {{ facility.qoh }}
                            </option>
                          </select>
                          <div
                            v-if="loadingQoh"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                          >
                            <LoadingSpinner class="text-gray-500" />
                          </div>
                          <div
                            v-if="loadingRestrictions"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                          >
                            <LoadingSpinner class="text-gray-500" />
                          </div>
                        </div>
                      </td>
                      <td class="checkbox-center">
                        <input
                          type="checkbox"
                          :disabled="!orderDetail.IsChecked"
                          :checked="orderDetail.IsChecked"
                          :title="orderDetail.facility_locking_actor"
                          @change="handleCheckboxChange(orderDetail)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <HttpError :error="error" />
      <!-- Footer -->
      <div class="flex flex-col justify-end py-2">
        <p class="text-faint">
          Clicking Save will update each line item to selected facility.
        </p>
        <div class="flex justify-end space-x-4">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
          <Button
            @click-handler="process"
            :disabled="processing || !validated"
            class="flex items-center space-x-2"
          >
            <span>Save</span>
            <LoadingSpinner v-if="processing" class="text-white" />
          </Button>
        </div>
      </div>
    </div>
  </Slideover>
</template>

<script>
import Button from "@/components/elements/Button.vue";
import Slideover from "@/components/layout/Slideover.vue";
import { ApiFactory } from "@/api/ApiFactory";

const Inventory = ApiFactory.get("inventories");
const OrderApi = ApiFactory.get("orders");
const Utilities = ApiFactory.get("utilities");

export default {
  name: "SelectFacilityVBI",
  components: {
    Button,
    Slideover,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
    shipState: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      skus: [],
      skuqoh: null,
      processing: false,
      loadingRestrictions: false,
      loadingQoh: false,
      error: null,
      validated: false,
      products: [],
    };
  },
  mounted() {
    console.log(this.order);
    this.loadQoh();
    this.initializeSelectedFacilities();
    this.validateForRestrictions();
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    orderDetails() {
      if (!this.order.details) {
        return [];
      }
      return this.order.details.filter((orderDetail) => {
        if (this.isProductCancelled(orderDetail)) {
          return false;
        }

        if (orderDetail.Adjustment != "0") {
          return false;
        }

        if (orderDetail.Type == "P") {
          return false;
        }

        let sku = orderDetail.SKU.toLowerCase();
        for (let item of ["warranty", "giftcert", "giftwrap", "routeins"]) {
          if (sku.includes(item)) {
            return false;
          }
        }
        return true;
      });
    },
  },
  watch: {},
  methods: {
    isShipped(orderDetail) {
      return orderDetail.DateShipped !== null;
    },
    // This method will only trigger when user unchecks the checkbox.
    handleCheckboxChange(orderDetail) {
      orderDetail.IsChecked = false;
    },
    initializeSelectedFacilities() {
      if (this.order.details) {
        this.order.details.forEach((orderDetail) => {
          orderDetail.SelectedFacilityId = orderDetail.facility_id;
          orderDetail.IsLocked = !(
            orderDetail.facility_locking_actor === null ||
            orderDetail.facility_locking_actor === undefined ||
            orderDetail.facility_locking_actor.trim() === ""
          );
          orderDetail.IsChecked = orderDetail.IsLocked;
        });
      }
    },
    async loadQoh() {
      this.loadingQoh = true;
      for (const orderDetail of this.orderDetails) {
        const response = await Inventory.variantCountsByFacility(orderDetail.variant_id);
        const variantCountsByFacility = response.data.body;

        if (
          !variantCountsByFacility ||
          variantCountsByFacility.length === 0 ||
          !variantCountsByFacility[0] ||
          variantCountsByFacility[0] === undefined
        ) {
          orderDetail.facilities = [];
        } else {
          orderDetail.facilities = [
            { id: 1, name: "Fort Mill, SC", qoh: this.getQohFromVariantCounts(1, variantCountsByFacility) },
            { id: 3, name: "Dundee, OR", qoh: this.getQohFromVariantCounts(3, variantCountsByFacility) },
          ];
        }
      }
      this.loadingQoh = false;
    },
    getQohFromVariantCounts(facilityId, variantCountsByFacility) {
      const variantCountAtFacility = variantCountsByFacility.find((item) => item.facility_id == facilityId);
      if (variantCountAtFacility) {
        return parseInt(variantCountAtFacility.total_for_sale_qoh) - parseInt(variantCountAtFacility.committed_quantity);
      } else {
        return 0;
      }
    },
    async validateForRestrictions() {
      try {
        for (const orderDetail of this.orderDetails) {
          const facility_id = parseInt(orderDetail.SelectedFacilityId);
          if (facility_id == null) {
            return;
          }
          this.loadingRestrictions = true;
          this.validated = false;
          this.error = null;

          // Change logic here after VBI State Restrictions is implemented
          const response =
            await Utilities.validateSkuStateRestrictionByFacility({
              skus: [orderDetail.SKU],
              facility_id: facility_id,
            });

          const res = response.data.body;
          const facility = res[orderDetail.SKU].find(
            (facilityRestrictions) =>
              facilityRestrictions.facility_id === facility_id
          );
          if (
            facility === undefined ||
            facility.state_restrictions.includes(this.shipState)
          ) {
            this.validated = false;
            const err = new Error("Failed validation");
            err.response = {
              data: {
                message: `${orderDetail.SKU} is restricted in ${this.shipState}`,
              },
            };
            throw err;
          }

          this.validated = true;
        }
      } catch (error) {
        this.error = error;
      } finally {
        this.loadingRestrictions = false;
      }
    },
    async process() {
      this.processing = true;

      try {
        const promises = this.orderDetails.map((orderDetail) => {
          // User has selected different facility_id than prior one for the order
          if (orderDetail.SelectedFacilityId !== orderDetail.facility_id) {
            return OrderApi.changeFacility(
              this.order.SourceOrderNumber,
              orderDetail.ItemNumber,
              {
                facility_id: orderDetail.SelectedFacilityId,
                actor: this.user.username,
                reason: "MANUAL",
              }
            );
          } else {
            // Order was locked / assigned facility manually previously but now user has unlocked it by unchecking the checkbox.
            // In this case, we need to clear actor on the order item so fulfillment routing assigns the facility
            if (orderDetail.IsChecked !== orderDetail.IsLocked) {
              return OrderApi.changeFacility(
                this.order.SourceOrderNumber,
                orderDetail.ItemNumber,
                {
                  facility_id: orderDetail.SelectedFacilityId,
                  actor: "",
                  reason: "MANUAL",
                }
              );
            } else {
              return Promise.resolve();
            }
          }
        });
        await Promise.all(promises).then(() => {
          this.handleSuccess();
        });
      } catch (err) {
        this.error = err;
      } finally {
        this.processing = false;
      }
    },
    async handleSuccess() {
      await OrderApi.validateStateRestriction(this.order.SourceOrderNumber, {
        params: { updateStatus: true },
      });
      this.$emit("toggle-slideover");
      this.$emit("refresh-order");
    },
    isProductCancelled(product) {
      return (
        product?.Canceled === 1 || product?.Product?.search(/^CANCELED/g) > -1
      );
    },
  },
};
</script>

<style scoped lang="scss">
th {
  @apply px-2 py-2 bg-gray-200 text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider;
}

td {
  @apply px-2 py-1 whitespace-no-wrap text-sm leading-5;
}

table {
  @apply text-gray-500;
}

.checkbox-center {
  text-align: center;
  vertical-align: middle;
}
.text-faint {
  color: #757575; /* Example color - adjust as needed */
  text-align: left;
}

.limited-width {
  max-width: 150px;
  word-wrap: break-word;
  white-space: normal; /* Allow line breaks */
}
</style>
